
import { TimeSlot, TimeSlotGroup } from '../types';
import { getDay, parse, isValid } from 'date-fns';

/**
 * Generates time slots based on doctor's configuration
 */
export const generateTimeSlots = (date: Date, doctor: { 
  slotDuration?: number; 
  weeklyOffDays?: number[];
  startTime?: string;
  endTime?: string;
}): TimeSlotGroup[] => {
  // Check if the selected date falls on doctor's off day
  const dayOfWeek = getDay(date);
  if (doctor.weeklyOffDays && doctor.weeklyOffDays.includes(dayOfWeek)) {
    return []; // Return empty array for off days
  }

  const slotDuration = doctor.slotDuration || 30;
  
  // Parse doctor's start and end time, default to 9 AM - 7 PM if not specified
  let startHour = 9;
  let startMinute = 0;
  let endHour = 19;
  let endMinute = 0;

  if (doctor.startTime && isValid(parse(doctor.startTime, 'HH:mm', new Date()))) {
    const [startH, startM] = doctor.startTime.split(':').map(Number);
    startHour = startH;
    startMinute = startM;
  }

  if (doctor.endTime && isValid(parse(doctor.endTime, 'HH:mm', new Date()))) {
    const [endH, endM] = doctor.endTime.split(':').map(Number);
    endHour = endH;
    endMinute = endM;
  }

  // Groups for morning, afternoon, evening
  const morningSlots: TimeSlot[] = [];
  const afternoonSlots: TimeSlot[] = [];
  const eveningSlots: TimeSlot[] = [];

  // Generate time slots for the entire day based on start and end times
  for (let hour = startHour; hour < endHour; hour++) {
    // For the first hour, start from the specified minute
    let startMinuteForHour = (hour === startHour) ? startMinute : 0;
    // For the last hour, end at the specified minute
    let endMinuteForHour = (hour === endHour - 1) ? endMinute : 60;

    for (let minute = startMinuteForHour; minute < endMinuteForHour; minute += slotDuration) {
      if (minute >= 60) continue; // Skip if we overflow to the next hour

      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      const period = hour >= 12 ? 'PM' : 'AM';
      const minuteStr = minute.toString().padStart(2, '0');
      const time = `${displayHour}:${minuteStr} ${period}`;
      const hourStr = hour.toString().padStart(2, '0');
      const id = `slot-${hourStr}:${minuteStr}`;
      
      const timeSlot = {
        id,
        time,
        available: true // Will be updated with API check
      };

      if (hour < 12) {
        morningSlots.push(timeSlot);
      } else if (hour < 16) {
        afternoonSlots.push(timeSlot);
      } else {
        eveningSlots.push(timeSlot);
      }
    }
  }

  const result: TimeSlotGroup[] = [];
  
  // Only add non-empty groups
  if (morningSlots.length > 0) {
    result.push({ label: "Morning", slots: morningSlots });
  }
  if (afternoonSlots.length > 0) {
    result.push({ label: "Afternoon", slots: afternoonSlots });
  }
  if (eveningSlots.length > 0) {
    result.push({ label: "Evening", slots: eveningSlots });
  }

  return result;
};

/**
 * Check if a date should be disabled based on doctor's availability
 */
export const isDateDisabled = (date: Date, doctor?: { weeklyOffDays?: number[] }, maxDays = 30) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // Allow selection for next maxDays days only
  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + maxDays);
  
  // Disable past dates or dates beyond maxDays
  if (date < today || date > maxDate) {
    return true;
  }
  
  // Disable weekly off days
  if (doctor && doctor.weeklyOffDays) {
    const dayOfWeek = getDay(date);
    return doctor.weeklyOffDays.includes(dayOfWeek);
  }
  
  return false;
};
